import { getTop3OfNations } from '@/api/book';
import DailyChart from '@/components/bookStatus/DailyChart';
import MonthlyChart from '@/components/bookStatus/MonthlyChart';
import NationList from '@/components/bookStatus/NationList';
import WeeklyChart from '@/components/bookStatus/WeeklyChart';
import { DATE_CATEGORY, nationCodeToName } from '@/constants/book';
import useUpdateUrlParams from '@/hooks/useUpateUrlParams';
import { DATE_INTERVAL } from '@/types/book';
import { format } from 'date-fns';
import { useState } from 'react';
import { useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import ICON_ARROW from '@/assets/book/icon_arrow.png';

function VisitorStatusSection() {
  const updateUrlParams = useUpdateUrlParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const dateType = searchParams.get('dateType') || 'DAILY';
  const endDate =
    searchParams.get('endDate') || format(new Date(), 'yyyy-MM-dd');

  const [nationList, setNationList] = useState<
    { code: string; name: string | undefined }[]
  >([]);

  const { data } = useQuery(['top3OfNationsList'], getTop3OfNations, {
    onSuccess: (data) => {
      const result = data.nations.map((nation) => nationCodeToName(nation));
      setNationList(result);
    },
  });

  const onChangeDateType = (value: DATE_INTERVAL) => {
    updateUrlParams({ endDate: format(new Date(), 'yyyy-MM-dd') });
    updateUrlParams({ dateType: value });
  };

  const onChangeEndDate = (date: string) => {
    updateUrlParams({ endDate: date });
  };

  const RenderChart = () => {
    switch (dateType) {
      case 'DAILY':
        return (
          <DailyChart
            endDate={endDate}
            setEndDate={onChangeEndDate}
            nations={data?.nations || []}
          />
        );
      case 'WEEKLY':
        return (
          <WeeklyChart
            endDate={endDate}
            setEndDate={onChangeEndDate}
            nations={data?.nations || []}
          />
        );
      case 'MONTHLY':
        return (
          <MonthlyChart
            endDate={endDate}
            setEndDate={onChangeEndDate}
            nations={data?.nations || []}
          />
        );
      default:
        return null;
    }
  };

  const onResetEndDate = () => {
    updateUrlParams({ endDate: format(new Date(), 'yyyy-MM-dd') });
  };

  return (
    <Section>
      <Header>
        <Title>예약자 내원 현황</Title>
        <Nav>
          <DateCategory>
            {DATE_CATEGORY.map(({ id, value, label }) => (
              <DateButton
                key={id}
                isSelected={dateType === value}
                onClick={() => onChangeDateType(value)}
              >
                {label}
              </DateButton>
            ))}
          </DateCategory>
          <NavRightSection>
            <NationList nations={nationList} />
            <TodayButton onClick={onResetEndDate}>
              오늘
              <img src={ICON_ARROW} alt='' width={10} />
            </TodayButton>
          </NavRightSection>
        </Nav>
      </Header>
      <RenderChart />
    </Section>
  );
}

const Section = styled.section`
  background-color: #fff;
  border-radius: 16px;
  padding: 22px 28px;
  width: 100%;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 32px;
`;

const Title = styled.h3`
  color: #3a3b3e;
  font-size: 18px;
  font-weight: 700;
  line-height: 27px;
  flex-shrink: 0;
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const NavRightSection = styled.div`
  display: flex;
  gap: 48px;
`;

const TodayButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 56px;
  height: 24px;
  background-color: #f4f8ff;
  border-radius: 8px;
  color: #5089f8;
  font-size: 14px;
  font-weight: 600;
  line-height: 21px;
  border: 1px solid #246cf6;
  padding: 0;
`;

const DateCategory = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
`;

const DateButton = styled.button<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 53px;
  height: 28px;
  color: ${({ isSelected }) => (isSelected ? '#fff' : '#777777')};
  font-size: 14px;
  font-weight: 600;
  line-height: 21px;
  border-radius: 100px;
  border: 1px solid ${({ isSelected }) => (isSelected ? '#5089f8' : '#CBCCCE')};
  background-color: ${({ isSelected }) => (isSelected ? '#5089F8' : '#fff')};
`;

export default VisitorStatusSection;
